import { Button, ButtonProps, CircularProgress, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { StatusVariant } from '~enums';
import {
	getBorderedBorderColor,
	getBorderedLabelColor,
	getButtonBackgroundColor,
	getButtonBorderColor,
	getButtonBorderHoverColor,
	getButtonHoverColor,
	getButtonLabelColor,
	getTextButtonHoverColor,
} from '~enums/statusVariant';

import ActionButtonAlignment from './actionButtonAlignment';
import ActionButtonStyle from './actionButtonStyle';
import Dimens from '../../themes/dimens';

export interface ActionButtonProps extends Omit<ButtonProps, 'style' | 'variant'> {
	label?: string;
	loading?: boolean;
	disabled?: boolean;
	style?: ActionButtonStyle;
	variant?: StatusVariant;
	alignment?: ActionButtonAlignment;
	textTransform?: React.CSSProperties['textTransform'];
}

interface VariantDefinition {
	backgroundColor: string;
	hoverBackgroundColor: string;
	color: string;
	borderColor?: string;
	hoverBorderColor?: string;
	iconMargin: string;
}

const iconSizePx = 20;
const iconSize = Dimens.fixed(iconSizePx);

const getVariantDefinition = (
	style: ActionButtonStyle,
	variant: StatusVariant,
	palette: Theme['palette']
): VariantDefinition => {
	switch (style) {
		case ActionButtonStyle.Standard:
			return {
				backgroundColor: getButtonBackgroundColor(variant, palette),
				hoverBackgroundColor: getButtonHoverColor(variant, palette),
				color: getButtonLabelColor(variant, palette),
				borderColor: getButtonBorderColor(variant, palette),
				hoverBorderColor: getButtonBorderHoverColor(variant, palette),
				iconMargin: Dimens.spacingXs,
			};
		case ActionButtonStyle.Bordered:
			return {
				backgroundColor: 'transparent',
				hoverBackgroundColor: getButtonBackgroundColor(variant, palette),
				color: getBorderedLabelColor(variant, palette),
				borderColor: getBorderedBorderColor(variant, palette),
				hoverBorderColor: getBorderedLabelColor(variant, palette),
				iconMargin: Dimens.spacingXs,
			};
		case ActionButtonStyle.Text:
			return {
				backgroundColor: 'transparent',
				hoverBackgroundColor: getTextButtonHoverColor(variant, palette),
				color: getBorderedLabelColor(variant, palette),
				borderColor: 'transparent',
				hoverBorderColor: 'transparent',
				iconMargin: Dimens.spacingMd,
			};
	}
};

const ActionButton = ({
	label,
	loading = false,
	disabled = false,
	style = ActionButtonStyle.Standard,
	variant = StatusVariant.Neutral,
	alignment = ActionButtonAlignment.Center,
	textTransform = 'none',
	sx,
	startIcon,
	endIcon,
	...rest
}: ActionButtonProps) => {
	const theme = useTheme();
	const variantDefinition = getVariantDefinition(style, variant, theme.palette);
	const isBordered = variantDefinition.borderColor !== undefined;
	const hasLabel = Boolean(label);
	const justifyContent =
		alignment == ActionButtonAlignment.Start ? 'start'
		: alignment == ActionButtonAlignment.Center ? 'center'
		: 'end';
	const buttonContent =
		loading ?
			<CircularProgress
				size={iconSizePx}
				sx={{ color: variantDefinition.color, marginY: Dimens.spacingMd }}
			/>
		: hasLabel ?
			<Typography
				variant='labelSm'
				sx={{
					color: variantDefinition.color,
					textTransform,
					marginY: Dimens.fixed(9), // Account for border
				}}
			>
				{label}
			</Typography>
		:	null;

	return (
		<Button
			disableElevation
			variant={isBordered ? 'outlined' : 'contained'}
			disabled={disabled || loading}
			startIcon={startIcon}
			endIcon={endIcon}
			sx={{
				justifyContent: justifyContent,
				alignItems: 'center',
				alignContent: 'center',
				display: 'flex',
				color: variantDefinition.color,
				backgroundColor: variantDefinition.backgroundColor,
				borderColor: variantDefinition.borderColor,
				borderRadius: Dimens.radiusMd,
				paddingX: hasLabel ? Dimens.fixed(9) : Dimens.fixed(7), // Accounting for border
				paddingY: 0,
				gap: variantDefinition.iconMargin,
				minWidth: 'unset',
				height: 'unset',
				'& .MuiButton-startIcon, .MuiButton-endIcon': {
					marginLeft: Dimens.spacingNone,
					marginRight: Dimens.spacingNone,
					marginTop: Dimens.fixed(7),
					marginBottom: Dimens.fixed(7),
					display: 'flex',
					alignContent: 'center',
					justifyContent: 'center',
				},
				'& .MuiButton-startIcon > *, .MuiButton-endIcon > *': {
					width: iconSize,
					height: iconSize,
				},
				'&:hover': {
					backgroundColor: variantDefinition.hoverBackgroundColor,
					borderColor: variantDefinition.hoverBorderColor ?? variantDefinition.borderColor,
				},
				'&.Mui-disabled': {
					opacity: 0.5,
				},
				...sx,
			}}
			{...rest}
		>
			{buttonContent}
		</Button>
	);
};

export default ActionButton;
